import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Flex,
  Center,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Spinner,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Checkbox,
  FormControl,
  FormLabel,
  Icon,
  Switch,
  Portal,
  Divider,
  Tag,
} from "@chakra-ui/react";
import {
  SearchIcon,
  ChevronDownIcon,
  DeleteIcon,
  CopyIcon,
  AddIcon,
  EditIcon,
} from "@chakra-ui/icons";
import { Header } from "build-ts/metadata";
import { deleteAnalyst, cloneAnalyst } from "services/AnalystService";
import { updateHeader } from "services/MetadataServices";
import showToast from "hooks/useCustomToast";
import { useAnalystChat } from "context/AnalystChatContext";
import { BsRobot } from "react-icons/bs";
import { UserProto } from "build-ts/interface";
import { useUser } from "context/UserContext";
import { updateUser } from "services/UserService";

interface AnalystCardProps {
  header: Header;
  isActive: boolean;
  isLoadingAllJobs?: boolean;
  selectedAnalyst?: Header | null;
  defaultAnalystId: string;
  handleAnalystSelection: (header: Header) => void;
  handleRenameClick: (header: Header) => void;
  handleDuplicateClick: (header: Header) => void;
  handleDeleteClick: (header: Header) => void;
  handleUpdateDefaultAnalyst: (header: Header) => void;
}

const AnalystCard: React.FC<AnalystCardProps> = ({
  header,
  isActive,
  isLoadingAllJobs,
  selectedAnalyst,
  defaultAnalystId,
  handleAnalystSelection,
  handleRenameClick,
  handleDuplicateClick,
  handleDeleteClick,
  handleUpdateDefaultAnalyst,
}) => (
  <Box
    key={header.id}
    bg="white"
    borderRadius="xl"
    borderWidth="1px"
    borderColor={
      isActive
        ? "green.400"
        : selectedAnalyst?.id === header?.id ? "purple.400" : "purple.200"
    }
    boxShadow={isActive ? "0 0 0 2px rgba(72, 187, 120, 0.1)" : "none"}
    cursor="pointer"
    position="relative"
    overflow="hidden"
    _hover={{
      borderColor: isActive ? "green.500" : "purple.600",
      boxShadow: isActive
        ? "0 8px 16px rgba(72, 187, 120, 0.2)"
        : "0 8px 16px rgba(189, 67, 233, 0.12)",
      transform: "translateY(-4px)",
    }}
    onClick={() => handleAnalystSelection(header)}
  >
    <Box p={5} display="flex" flexDirection="column" height="100%">
      <Flex align="start" justify="space-between" mb={3}>
        <HStack spacing={3} flex="1">
          <Icon as={BsRobot} boxSize={7} color="purple.600" />
          <Box flex="1" minW={0}>
            <Text
              fontSize="sm"
              fontWeight="700"
              color="gray.800"
              noOfLines={2}
              mb={0}
              align="left"
            >
              {header.name || "Unnamed Analyst"}
            </Text>
          </Box>
        </HStack>

        <Menu>
          <MenuButton
            as={IconButton}
            icon={<ChevronDownIcon boxSize={5} />}
            variant="ghost"
            size="sm"
            color="gray.500"
            borderRadius="md"
            _hover={{ bg: "gray.100", color: "gray.700" }}
            onClick={(e) => e.stopPropagation()}
          />
          <Portal>
            <MenuList boxShadow="lg" borderRadius="lg">
              <MenuItem
                icon={<EditIcon boxSize={4} />}
                onClick={(e) => { e.stopPropagation(); handleRenameClick(header); }}
                fontWeight="500"
                fontSize="sm"
                _hover={{ bg: "gray.50" }}
              >
                Rename
              </MenuItem>
              <MenuItem
                icon={<CopyIcon boxSize={4} />}
                onClick={(e) => { e.stopPropagation(); handleDuplicateClick(header); }}
                fontWeight="500"
                fontSize="sm"
                _hover={{ bg: "gray.50" }}
              >
                Duplicate
              </MenuItem>
              <MenuItem
                icon={<DeleteIcon boxSize={4} />}
                onClick={(e) => { e.stopPropagation(); handleDeleteClick(header); }}
                color="red.600"
                fontWeight="500"
                fontSize="sm"
                _hover={{ bg: "red.50" }}
              >
                Delete
              </MenuItem>
            </MenuList>
          </Portal>
        </Menu>
      </Flex>

      <Box flex="1" mb={3}>
        {header.description ? (
          <Text fontSize="sm" color="gray.600" noOfLines={3} lineHeight="1.6" align="left">
            {header.description}
          </Text>
        ) : (
          <Text fontSize="sm" color="gray.400" fontStyle="italic" align="left">
            No description
          </Text>
        )}
      </Box>

      <Box mt="auto" pt={3} borderTop="1px" borderColor="gray.100">
        <Flex align="center" justify="space-between" wrap="wrap" gap={2}>
          <HStack spacing={2}>
            {isLoadingAllJobs ? (
              <HStack spacing={1.5} bg="purple.50" px={2} py={1} borderRadius="md">
                <Spinner size="xs" color="purple.600" thickness="2px" />
              </HStack>
            ) : (
              isActive && (
                <HStack spacing={1.5} bg="green.50" px={2} py={1} borderRadius="md">
                  <Box w="8px" h="8px" borderRadius="full" bg="green.500" />
                  <Text fontSize="10px" fontWeight="600" color="green.700" mb={0}>
                    Live
                  </Text>
                </HStack>
              )
            )}
            <HStack spacing={2} onClick={(e) => e.stopPropagation()}>
              <Text fontSize="xs" fontWeight="500" color="gray.600" ml={2} mb={0}>
                Default
              </Text>
              <Switch
                size="sm"
                colorScheme="green"
                isChecked={defaultAnalystId === header?.id}
                onChange={(e) => {
                  e.stopPropagation();
                  handleUpdateDefaultAnalyst(header);
                }}
                m={0}
              />
            </HStack>
          </HStack>

          {header.author_name && (
            <Text fontSize="xs" color="gray.500" noOfLines={1} mb={0}>
              by {header.author_name}
            </Text>
          )}
        </Flex>
      </Box>
    </Box>
  </Box>
);

interface AnalystSectionsProps {
  filteredHeaders: Header[];
  userId: string | undefined;
  activeAnalystsMap: { analystId: string; active: boolean; noOfActiveJobs: number; noOfTotalJobs: number }[];
  isLoadingAllJobs?: boolean;
  selectedAnalyst?: Header | null;
  defaultAnalystId: string;
  handleAnalystSelection: (header: Header) => void;
  handleRenameClick: (header: Header) => void;
  handleDuplicateClick: (header: Header) => void;
  handleDeleteClick: (header: Header) => void;
  handleUpdateDefaultAnalyst: (header: Header) => void;
}

const AnalystSections: React.FC<AnalystSectionsProps> = ({
  filteredHeaders,
  userId,
  activeAnalystsMap,
  isLoadingAllJobs,
  selectedAnalyst,
  defaultAnalystId,
  handleAnalystSelection,
  handleRenameClick,
  handleDuplicateClick,
  handleDeleteClick,
  handleUpdateDefaultAnalyst,
}) => {
  const yourAnalysts = filteredHeaders.filter(
    (h) => h.author_id === userId || h.id === defaultAnalystId
  );

  const othersAnalysts = filteredHeaders.filter(
    (h) => h.author_id !== userId && h.id !== defaultAnalystId
  );

  const cardProps = {
    isLoadingAllJobs,
    selectedAnalyst,
    defaultAnalystId,
    handleAnalystSelection,
    handleRenameClick,
    handleDuplicateClick,
    handleDeleteClick,
    handleUpdateDefaultAnalyst,
  };

  const renderSection = (title: string, headers: Header[]) => {
    if (headers.length === 0) return null;
    return (
      <Box>
        <HStack spacing={3} mb={4}>
          <Text fontSize="md" fontWeight="700" color="gray.700" mb={0}>
            {title}
          </Text>
          <Tag
            size="sm"
            colorScheme="purple"
            borderRadius="full"
            fontWeight="600"
            bg="purple.600"
            color="white"
          >
            {headers.length}
          </Tag>
          <Divider flex="1" borderColor="gray.200" />
        </HStack>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 3 }} spacing={6}>
          {headers.map((header) => {
            const isActive = activeAnalystsMap.find((a) => a.analystId === header.id)?.active || false;
            return (
              <AnalystCard
                key={header.id}
                header={header}
                isActive={isActive}
                {...cardProps}
              />
            );
          })}
        </SimpleGrid>
      </Box>
    );
  };

  return (
    <VStack spacing={8} align="stretch">
      {renderSection("Your Analysts", yourAnalysts)}
      {renderSection("Others' Analysts", othersAnalysts)}
    </VStack>
  );
};

interface ListAnalystsProps {
  analystHeaders: Header[];
  setAnalystHeaders?: (headers: Header[]) => void;
  selectedAnalyst?: Header | null;
  onAnalystSelection?: (header: Header) => void;
  onCreateNewAnalyst?: () => void;
  onBackToList?: () => void;
  isLoading?: boolean;
  isLoadingAllJobs?: boolean;
  activeAnalystsMap?: {
    analystId: string;
    active: boolean;
    noOfActiveJobs: number;
    noOfTotalJobs: number;
  }[];
}

const ListAnalysts: React.FC<ListAnalystsProps> = ({
  analystHeaders,
  setAnalystHeaders,
  selectedAnalyst,
  onAnalystSelection,
  onCreateNewAnalyst,
  onBackToList,
  isLoading,
  isLoadingAllJobs,
  activeAnalystsMap = [],
}) => {
  const { setAnalystsList } = useAnalystChat();

  const { user, userId, defaultAnalystId, setDefaultAnalystId } = useUser();
  // Search and filtering logic
  const [filteredHeaders, setFilteredHeaders] = useState<Header[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal states
  const [editingAnalyst, setEditingAnalyst] = useState<Header | null>(null);
  const [newAnalystName, setNewAnalystName] = useState("");
  const [deletingAnalyst, setDeletingAnalyst] = useState<Header | null>(null);
  const [duplicatingAnalyst, setDuplicatingAnalyst] = useState<Header | null>(null);

  // Duplicate modal states
  const [duplicateAnalystName, setDuplicateAnalystName] = useState("");
  const [copyScenarios, setCopyScenarios] = useState(true);
  const [copyNotes, setCopyNotes] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const {
    isOpen: isRenameOpen,
    onOpen: onRenameOpen,
    onClose: onRenameClose,
  } = useDisclosure();

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const {
    isOpen: isDuplicateOpen,
    onOpen: onDuplicateOpen,
    onClose: onDuplicateClose,
  } = useDisclosure();

  const cancelRef = useRef<HTMLButtonElement>(null);

  // Filter headers based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredHeaders(analystHeaders);
    } else {
      const filtered = analystHeaders?.filter((header) =>
        header?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredHeaders(filtered);
    }
  }, [analystHeaders, searchTerm]);

  const handleAnalystSelection = (header: Header) => {
    if (onAnalystSelection) {
      onAnalystSelection(header);
    }
  };

  const handleCreateNewAnalyst = () => {
    if (onCreateNewAnalyst) {
      onCreateNewAnalyst();
    }
  };

  const handleRenameClick = (header: Header) => {
    setEditingAnalyst(header);
    setNewAnalystName(header.name || "");
    onRenameOpen();
  };

  const handleDeleteClick = (header: Header) => {
    setDeletingAnalyst(header);
    onDeleteOpen();
  };

  const handleDuplicateClick = (header: Header) => {
    if (!header?.id) return;

    setDuplicatingAnalyst(header);
    setDuplicateAnalystName(`${header.name || ""} (Copy)`);
    setCopyScenarios(true);
    setCopyNotes(false);
    onDuplicateOpen();
  };

  const handleConfirmDuplicate = async () => {
    if (!duplicatingAnalyst?.id || !duplicateAnalystName.trim()) return;

    try {
      setIsDuplicating(true);

      // Clone the analyst
      const clonedAnalyst = await cloneAnalyst(
        duplicatingAnalyst.id,
        duplicateAnalystName.trim(),
        copyScenarios, // cloneScenarios
        copyNotes, // cloneNotes
        false, // cloneJobs
        true // save
      );

      if (clonedAnalyst && clonedAnalyst.header) {
        // Add the cloned analyst to the top of the list
        const updatedHeaders = [clonedAnalyst.header, ...analystHeaders];
        setAnalystHeaders?.(updatedHeaders);

        // Select the cloned analyst with preloaded data to avoid redundant API call
        if (onAnalystSelection) {
          onAnalystSelection(clonedAnalyst.header);
        }

        showToast("Analyst duplicated successfully", "", "success");
      }
    } catch (error) {
      showToast("Failed to duplicate analyst", "", "error");
    } finally {
      setIsDuplicating(false);
      setDuplicatingAnalyst(null);
      onDuplicateClose();
    }
  };

  const handleConfirmRename = async () => {
    if (!editingAnalyst || !newAnalystName.trim() || !editingAnalyst.id || !editingAnalyst.type) return;

    try {
      const updatedHeader = await updateHeader(
        editingAnalyst.id,
        newAnalystName.trim(),
        editingAnalyst.description || "",
        editingAnalyst.type
      );

      if (updatedHeader) {
        // Update the local state
        const updatedHeaders = analystHeaders.map(header =>
          header?.id === editingAnalyst?.id
            ? { ...header, name: newAnalystName.trim() }
            : header
        );
        setAnalystHeaders?.(updatedHeaders);
        setAnalystsList(updatedHeaders);

        // Update selected analyst if it's the one being renamed
        if (selectedAnalyst?.id === editingAnalyst?.id) {
          const updatedSelectedHeader = { ...editingAnalyst, name: newAnalystName.trim(), description: editingAnalyst.description || "" };
          onAnalystSelection?.(updatedSelectedHeader);
        }

        showToast("Analyst renamed successfully", "", "success");
      }
    } catch (error) {
      showToast("Failed to rename analyst", "", "error");
    } finally {
      onRenameClose();
      setEditingAnalyst(null);
      setNewAnalystName("");
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingAnalyst?.id) return;

    try {
      await deleteAnalyst(deletingAnalyst?.id);

      // Remove the deleted analyst from the list
      const updatedHeaders = analystHeaders.filter(h => h.id !== deletingAnalyst.id);
      setAnalystHeaders?.(updatedHeaders);
      setAnalystsList(updatedHeaders); // this list is being used in ChatPage.tsx

      // If the deleted analyst was selected, go back to list view
      if (selectedAnalyst?.id === deletingAnalyst.id) {
        onBackToList?.();
      }

      showToast("Analyst deleted successfully", "", "success");
      onDeleteClose();
    } catch (error) {
      showToast("Failed to delete analyst", "", "error");
    }
  };

  const handleUpdateDefaultAnalyst = async (analystHeader: Header) => {
    // if this analyst is already default, unset it
    const isCurrentlyDefault = defaultAnalystId === analystHeader.id;
    const newDefaultAnalystId = isCurrentlyDefault ? "" : (analystHeader.id || "");
    const clientObject = newDefaultAnalystId ? { defaultAnalystId: newDefaultAnalystId } : {};

    // update request
    const userRequest = UserProto.create({
      header: user?.header,
      email: user?.email,
      is_admin: user?.is_admin,
      client_object_json: JSON.stringify(clientObject),
    });

    const updatedUser = await updateUser(userRequest);

    if (updatedUser) {
      setDefaultAnalystId(newDefaultAnalystId);
      if (newDefaultAnalystId) {
        showToast(`${analystHeader.name} set as default analyst`, "", "success");
      } else {
        showToast("Default analyst removed", "", "success");
      }
    }
  };

  return (
    <>
      <Box
        height="100%"
        bg="white"
        overflowY="auto"
      >
        <Box maxW="1400px" mx="auto" px={8} py={4}>
          <VStack spacing={4} align="stretch">
            {/* Header Section */}
            <Box>
              <Flex justify="space-between" align="center" mb={1} mt={3}>
                <Box>
                  <HStack spacing={3} mb={2}>
                    <Icon as={BsRobot} boxSize={9} color="purple.700" />
                    <Text
                      fontSize="2xl"
                      fontWeight="700"
                      color="gray.800"
                      letterSpacing="-0.5px"
                      mb={0}
                    >
                      Analysts
                    </Text>
                  </HStack>
                  <Text fontSize="md" color="gray.600" mt={0} align="left">
                    Create and manage AI analysts that have access to different metrics, analyses and jobs.
                  </Text>
                </Box>
                <Button
                  leftIcon={<AddIcon boxSize={3} />}
                  onClick={handleCreateNewAnalyst}
                  size="lg"
                  bg="purple.600"
                  color="white"
                  px={6}
                  _hover={{
                    bg: "purple.700",
                    transform: "translateY(-1px)",
                    boxShadow: "lg"
                  }}
                  _active={{
                    bg: "purple.800",
                    transform: "translateY(0)"
                  }}
                  transition="all 0.2s"
                  fontWeight="600"
                  boxShadow="md"
                >
                  New Analyst
                </Button>
              </Flex>

              {/* Search Bar */}
              <InputGroup size="lg" maxW="500px">
                <InputLeftElement pointerEvents="none" height="full">
                  <SearchIcon color="gray.400" boxSize={5} />
                </InputLeftElement>
                <Input
                  placeholder="Search analysts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  bg="gray.50"
                  border="1px"
                  borderColor="gray.200"
                  fontSize="md"
                  _hover={{
                    borderColor: "gray.300",
                    bg: "white"
                  }}
                  _focus={{
                    borderColor: "purple.400",
                    boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
                    bg: "white"
                  }}
                  _placeholder={{
                    color: "gray.400"
                  }}
                />
              </InputGroup>
              {/* Analyst Count */}
              <Text fontSize="sm" color="gray.500" mt={4} ml={1} fontWeight="600" align="left">
                {"Showing "}
                {filteredHeaders.length} {filteredHeaders.length === 1 ? "analyst" : "analysts"}
                {searchTerm && " found"}
              </Text>
            </Box>

            {/* Analyst Grid */}
            <Box>
              {isLoading ? (
                <Center py={9}>
                  <VStack spacing={4}>
                    <Spinner size="xl" color="purple.600" thickness="3px" />
                    <Text color="gray.600" fontSize="md">Loading analysts...</Text>
                  </VStack>
                </Center>
              ) : filteredHeaders?.length === 0 ? (
                <Center py={9}>
                  <VStack spacing={3}>
                    <Box
                      p={4}
                      bg="gray.100"
                      borderRadius="full"
                    >
                      <SearchIcon boxSize={8} color="gray.400" />
                    </Box>
                    <Text color="gray.600" fontSize="lg" fontWeight="500">
                      {searchTerm
                        ? "No analysts found"
                        : "No analysts yet"}
                    </Text>
                    <Text color="gray.500" fontSize="sm">
                      {searchTerm
                        ? "Try adjusting your search"
                        : "Click 'New Analyst' to create your first analyst"}
                    </Text>
                  </VStack>
                </Center>
              ) : (
                <AnalystSections
                  filteredHeaders={filteredHeaders}
                  userId={userId}
                  activeAnalystsMap={activeAnalystsMap}
                  isLoadingAllJobs={isLoadingAllJobs}
                  selectedAnalyst={selectedAnalyst}
                  defaultAnalystId={defaultAnalystId}
                  handleAnalystSelection={handleAnalystSelection}
                  handleRenameClick={handleRenameClick}
                  handleDuplicateClick={handleDuplicateClick}
                  handleDeleteClick={handleDeleteClick}
                  handleUpdateDefaultAnalyst={handleUpdateDefaultAnalyst}
                />
              )}
            </Box>
          </VStack>
        </Box>
      </Box>

      {/* Rename Analyst Modal */}
      <Modal isOpen={isRenameOpen} onClose={onRenameClose}>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl" mx={4}>
          <ModalHeader>Rename Analyst</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" color="gray.700">Analyst Name</FormLabel>
              <Input
                value={newAnalystName}
                onChange={(e) => setNewAnalystName(e.target.value)}
                placeholder="Enter new analyst name"
                size="lg"
                _focus={{
                  borderColor: "purple.400",
                  boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)"
                }}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onRenameClose}>
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleConfirmRename}
              isDisabled={!newAnalystName.trim()}
            >
              Rename
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Analyst Confirmation */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay backdropFilter="blur(4px)">
          <AlertDialogContent borderRadius="xl" mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Analyst
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete <Text as="span" fontWeight="600">"{deletingAnalyst?.name}"</Text>?
              This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose} variant="ghost">
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Duplicate Analyst Modal */}
      <Modal isOpen={isDuplicateOpen} onClose={onDuplicateClose}>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl" mx={4}>
          <ModalHeader>Duplicate Analyst</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={5} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">Analyst Name</FormLabel>
                <Input
                  value={duplicateAnalystName}
                  onChange={(e) => setDuplicateAnalystName(e.target.value)}
                  placeholder="Enter analyst name"
                  size="lg"
                  _focus={{
                    borderColor: "purple.400",
                    boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)"
                  }}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={3}>Options</FormLabel>
                <VStack align="start" spacing={3}>
                  <Checkbox
                    isChecked={copyScenarios}
                    onChange={(e) => setCopyScenarios(e.target.checked)}
                    colorScheme="purple"
                    size="md"
                  >
                    <Text fontSize="sm" m={0}>Copy Scenarios</Text>
                  </Checkbox>

                  <Checkbox
                    isChecked={copyNotes}
                    onChange={(e) => setCopyNotes(e.target.checked)}
                    colorScheme="purple"
                    size="md"
                  >
                    <Text fontSize="sm" m={0}>Copy Notes</Text>
                  </Checkbox>
                </VStack>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDuplicateClose}>
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleConfirmDuplicate}
              isDisabled={!duplicateAnalystName.trim()}
              isLoading={isDuplicating}
            >
              Duplicate
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ListAnalysts;